// src/utils/generarContratoAliadoPDF.js
// Genera el PDF del "Contrato del Aliado" con los T&C vigentes + datos del
// negocio + timestamp de aceptación + IP. Devuelve { blob, hash } donde
// hash es el SHA-256 hex del contenido del PDF.
//
// Simetría con utils/generarContratoPDF.js (para afiliados personas):
// mismo estilo visual, mismo enfoque de hash como huella para trazabilidad.

import { jsPDF } from 'jspdf';

const VERDE       = [26, 77, 46];
const VERDE2      = [46, 125, 50];
const DORADO      = [200, 168, 75];
const GRIS        = [100, 100, 100];
const NEGRO       = [30, 30, 30];
const GRIS_CLARO  = [240, 240, 240];

const ENTIDAD = {
  nombre:    'FEDERACIÓN NACIONAL AGROINDUSTRIA AMBIENTAL Y PRODUCCIÓN LIMPIA — FENAGRO APL',
  nit:       '901026305-2',
  direccion: 'Carrera 96 G No 22 G 21, Bogotá D.C., Colombia',
  telefono:  '3007765400',
  email:     'info@fenagro-oficial.org',
};

function addHeader(doc, pageNum) {
  doc.setFillColor(...VERDE);
  doc.rect(0, 0, 210, 18, 'F');
  doc.setFillColor(...DORADO);
  doc.rect(0, 18, 210, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('FENAGRO APL', 14, 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(`NIT: ${ENTIDAD.nit}  ·  ${ENTIDAD.direccion}`, 14, 14);
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  doc.text(`Pág. ${pageNum}`, 195, 14, { align: 'right' });
}

function addFooter(doc) {
  const h = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setTextColor(...GRIS);
  doc.text(
    `Documento generado electrónicamente. ${ENTIDAD.email} · Tel ${ENTIDAD.telefono}`,
    105, h - 6, { align: 'center' }
  );
}

/**
 * Muy simple parser Markdown → líneas para jsPDF.
 * Soporta: # H1, ## H2, listas con "- " o "1. ", y párrafos.
 * No es una implementación completa; suficiente para T&C que ya redactamos.
 */
function renderMarkdown(doc, md, opts) {
  const { x, maxWidth } = opts;
  let y = opts.y;
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 20;

  const lines = md.split('\n');
  let pageNum = 1;

  const nuevaPaginaSiHaceFalta = (alturaProxLinea) => {
    if (y + alturaProxLinea > pageHeight - bottomMargin) {
      addFooter(doc);
      doc.addPage();
      pageNum += 1;
      addHeader(doc, pageNum);
      y = 30;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line === '') { nuevaPaginaSiHaceFalta(4); y += 3; continue; }
    if (line.startsWith('---')) { nuevaPaginaSiHaceFalta(6); doc.setDrawColor(...GRIS_CLARO); doc.line(x, y, x + maxWidth, y); y += 5; continue; }

    // H1
    if (line.startsWith('# ')) {
      nuevaPaginaSiHaceFalta(10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...VERDE);
      const txt = line.substring(2);
      doc.text(txt, x, y);
      y += 8;
      continue;
    }
    // H2
    if (line.startsWith('## ')) {
      nuevaPaginaSiHaceFalta(8);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...VERDE2);
      const txt = line.substring(3);
      doc.text(txt, x, y);
      y += 6;
      continue;
    }
    // Lista
    if (/^(-|\*|\d+\.)\s/.test(line)) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(...NEGRO);
      const bullet = line.match(/^(\d+\.|[-*])\s/)[0];
      const contenido = line.substring(bullet.length);
      const wrap = doc.splitTextToSize(contenido, maxWidth - 6);
      for (let i = 0; i < wrap.length; i++) {
        nuevaPaginaSiHaceFalta(5);
        if (i === 0) doc.text(bullet.trim(), x, y);
        doc.text(wrap[i], x + 6, y);
        y += 5;
      }
      continue;
    }
    // Párrafo (elimina ** para negrita simple)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...NEGRO);
    const clean = line.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/`(.+?)`/g, '$1');
    const wrap = doc.splitTextToSize(clean, maxWidth);
    for (const w of wrap) {
      nuevaPaginaSiHaceFalta(5);
      doc.text(w, x, y);
      y += 5;
    }
  }
  return { y, pageNum };
}

async function sha256Hex(arrayBuffer) {
  const buf = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const bytes = new Uint8Array(buf);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

/**
 * Genera el PDF del contrato del aliado.
 * @param {object} params
 * @param {string} params.tycTitulo    — Titulo del T&C vigente
 * @param {string} params.tycContenido — Cuerpo del T&C vigente (Markdown)
 * @param {number} params.tycVersion   — Versión del T&C aceptado
 * @param {object} params.aliado       — Datos del aliado del wizard (razonSocial, nit, representanteLegal, cedula, email)
 * @param {Date}   params.fechaAceptacion — Momento de la aceptación
 * @returns {Promise<{ blob: Blob, hash: string, tamanioBytes: number, urlPreview: string }>}
 */
export async function generarContratoAliadoPDF({
  tycTitulo, tycContenido, tycVersion, aliado, fechaAceptacion,
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  addHeader(doc, 1);

  // Portada breve
  doc.setFontSize(16);
  doc.setTextColor(...VERDE);
  doc.setFont('helvetica', 'bold');
  doc.text('Contrato de Aliado Comercial', 105, 32, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRIS);
  doc.text(`Versión aceptada de T&C: ${tycVersion}`, 105, 39, { align: 'center' });

  // Datos del aliado
  doc.setFillColor(248, 250, 248);
  doc.roundedRect(14, 45, 182, 34, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setTextColor(...NEGRO);
  const lbl = (t, v, x, y) => {
    doc.setFont('helvetica', 'bold'); doc.text(t, x, y);
    doc.setFont('helvetica', 'normal'); doc.text(String(v || '—'), x + 45, y);
  };
  lbl('Razón social:',    aliado.razonSocial,       18, 53);
  lbl('NIT:',              aliado.nit,               18, 59);
  lbl('Representante:',    aliado.representanteLegal,18, 65);
  lbl('Cédula rep.:',      aliado.cedula,            18, 71);
  lbl('Email:',            aliado.email,             18, 77);

  // Fecha aceptación
  doc.setFontSize(8.5);
  doc.setTextColor(...GRIS);
  const fechaTxt = fechaAceptacion.toLocaleString('es-CO', { dateStyle: 'full', timeStyle: 'medium' });
  doc.text(`Aceptado electrónicamente el ${fechaTxt}`, 14, 86);

  // Contenido T&C
  const { pageNum } = renderMarkdown(doc, tycContenido, {
    x: 14, y: 94, maxWidth: 182,
  });

  // Pie última página + bloque de firma
  addFooter(doc);
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...DORADO);
  doc.setLineWidth(0.5);
  doc.line(14, h - 30, 196, h - 30);
  doc.setFontSize(8);
  doc.setTextColor(...VERDE);
  doc.setFont('helvetica', 'bold');
  doc.text('ACEPTACIÓN ELECTRÓNICA', 105, h - 25, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...NEGRO);
  doc.text(`${aliado.representanteLegal} · CC ${aliado.cedula}`, 105, h - 20, { align: 'center' });
  doc.text(`Actuando como representante legal de: ${aliado.razonSocial}`, 105, h - 15, { align: 'center' });

  // Hash del PDF
  const arrayBuffer = doc.output('arraybuffer');
  const hash = await sha256Hex(arrayBuffer);
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

  return {
    blob,
    hash,
    tamanioBytes: blob.size,
    urlPreview: URL.createObjectURL(blob),
    paginas: pageNum,
  };
}
